/**
 * SiteDocumentsLink — small button shown on Bay View / Site Manager
 * that pops a list of INTERNAL site-rule attachments (PDFs, images)
 * for the current site. Lets FLDs pull up the depot map / SOPs from
 * the dock screen without leaving the app.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Map as MapIcon } from "lucide-react";
import { listSiteRuleSections } from "@/lib/api";

export default function SiteDocumentsLink({ siteId, siteName }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !siteId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await listSiteRuleSections(siteId);
      if (cancelled) return;
      // Surface anything internal + has files / image_url
      const out = [];
      for (const sec of data || []) {
        if (!sec.is_internal) continue;
        if (sec.section_type === "file_upload") {
          for (const f of sec.data?.files || []) {
            out.push({ id: `${sec.id}-${f.path || f.name}`, name: f.name, url: f.url });
          }
        } else if (sec.section_type === "site_map" && sec.data?.image_url) {
          out.push({ id: sec.id, name: sec.title || "Site map", url: sec.data.image_url });
        }
      }
      setDocs(out);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, siteId]);

  if (!siteId) return null;
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="open-site-documents"
      >
        <MapIcon className="h-4 w-4 mr-1.5" /> Site documents
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="site-documents-dialog">
          <DialogHeader>
            <DialogTitle>Site documents — {siteName || "site"}</DialogTitle>
          </DialogHeader>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : docs.length === 0 ? (
            <p className="text-sm text-slate-400 italic">
              No internal documents uploaded yet. Add them via
              {" "}<a href="/admin/clients" className="text-blue-600 hover:underline">Client settings → Site maps</a>.
            </p>
          ) : (
            <ul className="space-y-1">
              {docs.map((d) => (
                <li key={d.id}>
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-sm text-blue-700 hover:underline"
                    data-testid={`site-document-${d.id}`}
                  >
                    <FileText className="h-4 w-4" />
                    {d.name}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
